import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  ReactNode,
} from 'react';
import { useNavigate } from 'react-router-dom';
import { app } from '../firebase';
import {
  getAuth,
  signInWithPopup,
  setPersistence,
  GoogleAuthProvider,
  browserLocalPersistence,
  onAuthStateChanged,
  RecaptchaVerifier,
  signInWithPhoneNumber,
  signOut,
  Auth,
  UserCredential,
  User,
  ConfirmationResult,
} from 'firebase/auth';
import { getDatabase, ref, set, Database } from 'firebase/database';

// Add global interface for Window
declare global {
  interface Window {
    confirmationResult: any;
    recaptchaWidgetId: any;
  }
}

// Type definitions

interface FirebaseContextType {
  confirm: ConfirmationResult | null;
  user: User | undefined;
  correct: boolean;
  signInWithGoogle: () => Promise<UserCredential | undefined>;
  SigninPhone: (
    phoneNumber: string,
    recaptchaVerifier: RecaptchaVerifier,
  ) => Promise<ConfirmationResult | undefined>;
  setupCaptcha: (number: string) => Promise<void>;
  otpLogin: (code: string) => Promise<void>;
  logout: () => void;
  fireSignout: () => void;
  auth: any;
}

interface FireAuthProps {
  children: ReactNode;
}

const FirebaseContext = createContext<FirebaseContextType | null>(null);

export const useFirebase = (): FirebaseContextType | null =>
  useContext(FirebaseContext);

const auth = getAuth(app);
const database = getDatabase(app);

setPersistence(auth, browserLocalPersistence).catch((error) => {
  console.error('Error setting Firebase auth persistence:', error);
});

const FireAuth: React.FC<FireAuthProps> = (props) => {
  const [confirm, setConfirm] = useState<ConfirmationResult | null>(null);
  const [user, setUser] = useState<User | undefined>(undefined);
  const [correct, setCorrect] = useState<boolean>(true);
  const navigate = useNavigate();

  useEffect(() => {
    onAuthStateChanged(auth, (user) => {
      if (user) {
        setUser(user);
      } else {
        setUser(undefined);
      }
    });
  }, [navigate]);

  const fireSignout = () => {
    signOut(auth)
      .then(() => {})
      .catch((error) => {
        console.log('error', error);
      });
  };

  const SigninGoogle = async (): Promise<UserCredential | undefined> => {
    try {
      // Persistence is already set globally, no need to set it again
      const provider = new GoogleAuthProvider();
      return await signInWithPopup(auth, provider);
    } catch (error) {
      console.log('error', error);
      return undefined;
    }
  };

  const SigninGoogleOld = async (): Promise<UserCredential | undefined> => {
    // Persistence is already set globally, no need to set it again
    const provider = new GoogleAuthProvider();
    try {
      return await signInWithPopup(auth, provider);
    } catch (error) {
      // Handle Errors here.
      console.error('Google sign-in error:', error);
      return undefined;
    }
  };

  const putData = (key, data) => {
    set(ref(database, key), data);
  };

  const SigninPhone = async (
    number: string,
    appVerifier: RecaptchaVerifier,
  ): Promise<ConfirmationResult | undefined> => {
    try {
      const confirmationResult = await signInWithPhoneNumber(
        auth,
        number,
        appVerifier,
      );
      // SMS sent. Prompt user to type the code from the message, then sign the
      // user in with confirmationResult.confirm(code).
      window.confirmationResult = confirmationResult;
      setConfirm(confirmationResult);
      return confirmationResult;
    } catch (error) {
      // Error; SMS not sent
      throw Error((error as any)?.code || 'Unknown error');
    }
  };

  const setupCaptcha = async (number) => {
    const recaptchaVerifier = new RecaptchaVerifier(
      auth,
      'recaptcha-container',
      {
        size: 'invisible',
      },
    );

    try {
      await recaptchaVerifier.render().then((widgetId) => {
        window.recaptchaWidgetId = widgetId;
      });
      await SigninPhone(number, recaptchaVerifier);
    } catch (e) {
      throw Error(e as string);
    }
  };

  const otpLogin = async (code) => {
    try {
      if (confirm) await confirm.confirm(code);
      navigate('/');
    } catch (error) {
      // User couldn't sign in (bad verification code?)
      // ...
      setCorrect(false);
      // errors=error
      throw Error((error as any)?.code || 'Unknown error');
    }
  };

  return (
    <FirebaseContext.Provider
      value={{
        signInWithGoogle: SigninGoogle,
        SigninPhone,
        setupCaptcha,
        otpLogin,
        user,
        auth,
        confirm,
        logout: fireSignout,
        fireSignout,
        correct,
      }}
    >
      {props.children}
    </FirebaseContext.Provider>
  );
};

export default FireAuth;
